import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Select } from "@/components/elements/Select";
import { Grid, Stack } from "@mui/material";
import { FC, useCallback, useMemo } from "react";
import { TCompanyForm } from "@/features/company/types/CompanyForm";
import { useFormContext, useWatch } from "react-hook-form";
import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { CompanyFormTitle } from "@/features/company/components/company-info/CompanyFormTitle";
import { useGetMainIndustryList } from "@/api/services/global/master";
import { IndustryClassificationRow } from "@/features/company/components/company-info/IndustryClassificationRow";

type TCompanyIndustryFieldInfoProps = {
  readonly?: boolean;
};

export const CompanyIndustryFieldInfo: FC<TCompanyIndustryFieldInfoProps> = ({ readonly }) => {
  const { isLoading: isLoadingMainIndustry, mainIndustryOptions } = useGetMainIndustryList();
  const methods = useFormContext<TCompanyForm>();
  const { control } = methods;

  const {
    isLoading: isLoadingIndustry,
    companySpecificIndustryOptions,
    getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry,
  } = useGetCompanySpecificIndustryList();

  const industryId1 = useWatch({ control, name: "companySpecificIndustryId1" });
  const industryId2 = useWatch({ control, name: "companySpecificIndustryId2" });
  const industryId3 = useWatch({ control, name: "companySpecificIndustryId3" });

  const industryOptions1 = useMemo(() => {
    return companySpecificIndustryOptions;
  }, [companySpecificIndustryOptions]);

  const industryOptions2 = useMemo(() => {
    if (!industryId1) return companySpecificIndustryOptions;
    return companySpecificIndustryOptions.filter((opt) => opt.value !== industryId1);
  }, [companySpecificIndustryOptions, industryId1]);

  const industryOptions3 = useMemo(() => {
    const excludeIds = [industryId1, industryId2].filter(Boolean);
    if (excludeIds.length === 0) return companySpecificIndustryOptions;
    return companySpecificIndustryOptions.filter((opt) => !excludeIds.includes(opt.value));
  }, [companySpecificIndustryOptions, industryId1, industryId2]);

  const handleIndustry1Change = useCallback(
    (newValue: number | null) => {
      methods.setValue("companySpecificBusinessClassificationIds1", []);
      if (newValue && industryId2 === newValue) {
        methods.setValue("companySpecificIndustryId2", null);
        methods.setValue("companySpecificBusinessClassificationIds2", []);
      }
      if (newValue && industryId3 === newValue) {
        methods.setValue("companySpecificIndustryId3", null);
        methods.setValue("companySpecificBusinessClassificationIds3", []);
      }
    },
    [methods, industryId2, industryId3],
  );

  const handleIndustry2Change = useCallback(
    (newValue: number | null) => {
      methods.setValue("companySpecificBusinessClassificationIds2", []);
      if (newValue && industryId3 === newValue) {
        methods.setValue("companySpecificIndustryId3", null);
        methods.setValue("companySpecificBusinessClassificationIds3", []);
      }
    },
    [methods, industryId3],
  );

  const handleIndustry3Change = useCallback(() => {
    methods.setValue("companySpecificBusinessClassificationIds3", []);
  }, [methods]);

  return (
    <Stack gap={2}>
      <InnerBox title={<CompanyFormTitle text="業種・分野" />} showDivider={false}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={6}>
            <FormField control={control} name="mainIndustryId">
              <Select loading={isLoadingMainIndustry} options={mainIndustryOptions} />
            </FormField>
          </Grid>
          <Grid item xs={6}></Grid>
          <IndustryClassificationRow
            index={1}
            control={control}
            readonly={readonly}
            isLoadingIndustry={isLoadingIndustry}
            companySpecificIndustryOptions={industryOptions1}
            getClassificationOptions={getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry}
            onIndustryChange={handleIndustry1Change}
          />

          <IndustryClassificationRow
            index={2}
            control={control}
            readonly={readonly}
            isLoadingIndustry={isLoadingIndustry}
            companySpecificIndustryOptions={industryOptions2}
            getClassificationOptions={getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry}
            onIndustryChange={handleIndustry2Change}
          />

          <IndustryClassificationRow
            index={3}
            control={control}
            readonly={readonly}
            isLoadingIndustry={isLoadingIndustry}
            companySpecificIndustryOptions={industryOptions3}
            getClassificationOptions={getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry}
            onIndustryChange={handleIndustry3Change}
          />
        </Grid>
      </InnerBox>
    </Stack>
  );
};
